import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatApiError } from "../lib/api";
import { Leaf } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      nav(u.has_profile ? "/dashboard" : "/onboarding");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img
          src="https://images.unsplash.com/photo-1461468611824-46457c0e11fd?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A5D23]/60 to-transparent" />
        <div className="relative p-12 text-white h-full flex flex-col">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <img src="/logo.png" alt="Gracely" className="w-8 h-8 object-contain brightness-0 invert" />
            <span className="font-serif-display text-2xl">Healthy Gracely</span>
          </Link>
          <div className="mt-auto max-w-md">
            <p className="font-serif-display text-4xl leading-tight">{`"Kesehatanmu adalah perjalanan, bukan tujuan."`}</p>
            <p className="mt-4 text-white/80 text-sm">— Gracely</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <h1 className="font-serif-display text-4xl text-stone-900">{t("login_title")}</h1>
          <p className="text-stone-500 mt-2">{t("login_sub")}</p>
          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label className="text-stone-700">{t("email")}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 rounded-xl bg-stone-50 border-transparent focus:ring-2 focus:ring-[#4A5D23]/30" data-testid="login-email-input" />
            </div>
            <div>
              <Label className="text-stone-700">{t("password")}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1 rounded-xl bg-stone-50 border-transparent focus:ring-2 focus:ring-[#4A5D23]/30" data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#4A5D23] hover:bg-[#3A4A1C] text-white py-6 btn-pop" data-testid="login-submit-button">
              {loading ? "…" : t("submit_login")}
            </Button>
          </form>
          <p className="mt-6 text-sm text-stone-500">
            {t("no_account")} <Link to="/register" className="text-[#4A5D23] font-semibold" data-testid="link-to-register">{t("submit_register")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
